import { useQuery } from "@tanstack/react-query";
import type { Role } from "@titleverify/shared";
import { apiFetch } from "./api.js";
import { useAuth } from "./auth.js";

/**
 * Who the signed-in person is, ACCORDING TO OUR USERS TABLE.
 *
 * Extracted out of App.tsx so a page can read the role without importing the
 * app shell — App renders the pages, so a page importing back from App would
 * close an import cycle. Every caller shares the ["me"] key, so TanStack
 * de-duplicates: this adds no request no matter how many components ask.
 *
 * THE ROLE IS NEVER READ FROM A TOKEN CLAIM. Firebase custom claims are shaped
 * by console config and some user fields are self-writable; a role is an
 * authorization fact and lives in our `users` table (CLAUDE.md §4). This asks
 * the server, which is the only party that knows.
 *
 * And what it is FOR is presentation only. A role read here decides which
 * controls are worth rendering; it decides nothing about what may happen. The
 * server refuses every ops action to every role but admin regardless of what
 * any screen draws.
 */
export type Me = { userId: string; role: Role };

export function useMe() {
  const { getToken } = useAuth();
  return useQuery<Me>({
    queryKey: ["me"],
    queryFn: async () => apiFetch<Me>("/me", { token: await getToken() }),
    retry: false,
  });
}
